import { useEffect, useId, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Button, Skeleton } from '@cannect/new-components/atoms'
import { QuantityCounter } from '@cannect/new-components/molecules'
import { formatToBRL } from '@cannect/utils/formatters'
import { getAuthenticatedProductsList } from '@cannect/services/resources/products/requests'
import { ACOLHIMENTO_WHATSAPP_URL, MAX_QUANTITY_PER_ITEM } from '@cannect/services/resources/recurrency/constants'
import {
  useAddSubscriptionItem,
  useEligibility,
  useSubscriptionPreview
} from '@cannect/services/resources/recurrency/hooks'
import { formatApiDate, zipReasons } from '@cannect/services/resources/recurrency/helpers'
import type { SubscriptionDetail } from '@cannect/services/resources/recurrency/types'
import { subscriptionsCopy } from '../copy'
import { activeItems } from '../hooks/useEditDraft'
import { useSubscriptionAction } from '../hooks/useSubscriptionAction'
import { DialogShell } from './DialogShell'

const copy = subscriptionsCopy.addItem
const SEARCH_DEBOUNCE_MS = 400
const MIN_SEARCH = 3

type Props = { detail: SubscriptionDetail; open: boolean; onOpenChange: (open: boolean) => void }

const NotPrescribed = () => (
  <span>
    {copy.notPrescribed}.{' '}
    <a
      href={ACOLHIMENTO_WHATSAPP_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="font-semibold text-primary-700 underline">
      {subscriptionsCopy.delivery.talkToUs}
    </a>
  </span>
)

export const AddItemDialog = ({ detail, open, onOpenChange }: Props) => {
  const searchId = useId()
  const [search, setSearch] = useState('')
  const [term, setTerm] = useState('')
  const [selected, setSelected] = useState<{ productId: number; name: string } | null>(null)
  const [quantity, setQuantity] = useState(1)
  const [error, setError] = useState<{ code?: string; message: string } | null>(null)

  useEffect(() => {
    if (open) return
    setSearch('')
    setTerm('')
    setSelected(null)
    setQuantity(1)
    setError(null)
  }, [open])

  useEffect(() => {
    const timer = setTimeout(() => setTerm(search.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [search])

  // Sem filtro de recorrência na listagem (delta D5): busca por nome e confere elegibilidade.
  const products = useQuery({
    queryKey: ['recurrency', 'productSearch', term],
    queryFn: () =>
      getAuthenticatedProductsList('1', new URLSearchParams({ translated_name: term, limit: '20', page: '1' })),
    enabled: open && term.length >= MIN_SEARCH,
    staleTime: 60_000
  })
  const rows = products.data?.products?.rows || []
  const inSubscription = useMemo(() => new Set(activeItems(detail).map((item) => item.productId)), [detail])
  const candidateIds = rows.map((row) => row.id).filter((id) => !inSubscription.has(id))

  const eligibility = useEligibility(
    { productIds: candidateIds, quantities: candidateIds.map(() => 1), personId: detail.personId },
    { enabled: open && candidateIds.length > 0 }
  )

  const preview = useSubscriptionPreview(
    detail.id,
    open && selected ? { addItem: { productId: selected.productId, quantity } } : null
  )

  const action = useSubscriptionAction({
    detail,
    mutation: useAddSubscriptionItem(detail.id),
    event: 'recurrency_edit',
    eventPayload: () => ({ change: 'add_item' }),
    fieldErrorCodes: ['PRODUCT_NOT_PRESCRIBED', 'PRODUCT_ALREADY_IN_SUBSCRIPTION', 'QUANTITY_ABOVE_PRESCRIPTION'],
    onFieldError: (err) => setError({ code: err.code, message: err.message })
  })

  const eligibilityOf = (productId: number) =>
    eligibility.data?.items.find((item) => Number(item.productId) === Number(productId))

  return (
    <DialogShell
      open={open}
      onOpenChange={onOpenChange}
      title={copy.title}
      footer={
        <>
          <Button variant="outline_light" onClick={() => onOpenChange(false)}>
            {subscriptionsCopy.buttons.back}
          </Button>
          <Button
            disabled={!selected}
            isLoading={action.isPending}
            onClick={async () => {
              if (selected && (await action.run({ productId: selected.productId, quantity }))) onOpenChange(false)
            }}>
            {copy.confirm}
          </Button>
        </>
      }>
      <div className="flex flex-col gap-1">
        <label htmlFor={searchId} className="text-sm font-semibold text-neutral-800">
          {copy.search}
        </label>
        <input
          id={searchId}
          type="search"
          value={search}
          placeholder={copy.searchPlaceholder}
          onChange={(event) => {
            setSearch(event.target.value)
            setSelected(null)
            setError(null)
          }}
          className="h-11 rounded-md border border-solid border-neutral-300 px-3 text-sm"
        />
      </div>

      {term.length >= MIN_SEARCH && (
        <div aria-live="polite" className="flex flex-col gap-2">
          {products.isFetching && !rows.length && <Skeleton className="h-16 w-full" />}
          {!products.isFetching && !rows.length && <p className="m-0 text-sm text-neutral-600">{copy.noResults}</p>}
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {rows.map((row) => {
              const name = row.translated_name || row.name || 'Produto'
              const already = inSubscription.has(row.id)
              const item = eligibilityOf(row.id)
              const reasons = item ? zipReasons(item) : []
              const notPrescribed = item?.reasons?.includes('PRODUCT_NOT_PRESCRIBED')
              const eligible = !already && Boolean(item?.eligible)
              const isSelected = selected?.productId === row.id
              const image = row.media?.[0]?.path

              return (
                <li
                  key={row.id}
                  className="flex items-center gap-3 rounded-lg border border-solid border-neutral-100 p-2">
                  {image ? (
                    <img src={image} alt={name} className="h-10 w-10 shrink-0 rounded object-contain" />
                  ) : (
                    <span aria-hidden className="h-10 w-10 shrink-0 rounded bg-neutral-50" />
                  )}
                  <div className="flex min-w-0 flex-1 flex-col text-sm">
                    <span className="font-semibold text-neutral-800">{name}</span>
                    {already && <span className="text-neutral-600">{copy.alreadyIn}</span>}
                    {!already && item && !item.eligible && (
                      <span className="text-neutral-700">
                        {notPrescribed ? <NotPrescribed /> : reasons[0]?.message}
                      </span>
                    )}
                    {!already && !item && eligibility.isFetching && <Skeleton className="h-4 w-24" />}
                  </div>
                  {eligible && (
                    <Button
                      size="md"
                      variant={isSelected ? 'default' : 'outline_light'}
                      aria-pressed={isSelected}
                      onClick={() => {
                        setSelected({ productId: row.id, name })
                        setQuantity(1)
                        setError(null)
                      }}>
                      {isSelected ? 'Selecionado' : 'Selecionar'}
                    </Button>
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {selected && (
        <div className="flex flex-col gap-3 rounded-lg bg-neutral-50 p-3">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-semibold text-neutral-800">
              {copy.quantity} · {selected.name}
            </span>
            <QuantityCounter
              quantity={quantity}
              onChangeQuantity={(next) => setQuantity(Math.min(MAX_QUANTITY_PER_ITEM, Math.max(1, next)))}
            />
          </div>
          <div aria-live="polite" className="text-sm">
            {preview.data && (
              <dl className="m-0 flex flex-col gap-1">
                <div className="flex justify-between">
                  <dt>{subscriptionsCopy.preview.newTotal}</dt>
                  <dd className="font-semibold">{formatToBRL(preview.data.estimatedAmount ?? undefined)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt>{subscriptionsCopy.preview.nextCharge}</dt>
                  <dd>{formatApiDate(preview.data.nextChargeAt)}</dd>
                </div>
              </dl>
            )}
          </div>
        </div>
      )}

      {error && (
        <p role="alert" className="m-0 text-sm text-red-700">
          {error.code === 'PRODUCT_NOT_PRESCRIBED' ? <NotPrescribed /> : error.message}
        </p>
      )}
    </DialogShell>
  )
}
